// The fourteen customer-facing inspection areas of the ELE-MS-001 passenger checklist, as the
// engineer PWA presents them (Session ADM). Session ADL built this mapping for the customer PDF;
// the PWA now presents the SAME areas so the engineer, the customer document and the office all
// speak the same fourteen areas. This file is a READ-ONLY MIRROR of the server source of truth
// (server/src/pdf/templates/serviceSheet.ts SERVICE_AREAS_14), pinned byte-for-byte by
// server/src/modules/checklists/serviceAreasParity.test.ts -- change BOTH files together.
//
// Pure (no React) so the grouping, gating and result-shaping logic unit tests directly. The
// engineer flow built on these helpers closes the audit's F1 integrity hole: an item is only
// recorded as a pass after the engineer AFFIRMATIVELY confirms its area (one tap for a clean
// visit), never by silent default.
//
// Session BED: the five RETIRED interval items (loler_landing_locks, loler_safety_gear, loler_main_drive,
// loler_shaft_switches, loler_jack) stay listed in their old areas below, exactly as on the server, so a
// stored answer for one still groups under its old area when an old record is opened or amended. They are
// never offered on a new visit (the server's checklist no longer carries them).
import type { ChecklistItemDef, ChecklistStatus } from "./offline/types";

export const SERVICE_AREAS_14: { area: string; ids: string[] }[] = [
  {
    area: "Machine Room and Controller",
    ids: [
      "mr_access", "mr_access_ladder", "mr_ventilation", "mr_mains_switch", "mr_door_notices",
      "mr_clearance", "mr_fuses", "mr_rubber_mats", "mr_power_point", "mr_shock_notice",
      "mr_drawings", "mr_trap_door", "mr_guard_rail", "mr_lifting_beams", "mr_fire_extinguisher",
      "mr_equipment_only", "pm_control_panel", "pm_circuit_breaker", "pm_wiring",
    ],
  },
  {
    area: "Motor, Drive and Brake System",
    ids: [
      "mr_handwheel", "mr_flywheel_yellow", "mr_winding_instructions", "mr_brake_lever",
      "mr_standing_area", "pm_main_drive", "pm_brake_unit", "pm_hydraulic_oil", "loler_main_drive",
    ],
  },
  {
    // Session ADM (Director-directed): the hydraulic jack/cylinder and hose/pipework checks
    // sit here so a hydraulic lift's suspension area reads meaningfully (mirrors the server).
    area: "Ropes, Suspension, Sheaves and Hydraulic Rams",
    ids: [
      "mr_sheave_guard", "mr_diverter_yellow", "toc_sheave_guards", "toc_overhead_pulleys",
      "pit_rope_terminations", "pit_cwt_screen", "pm_pulley_diverter", "pm_suspension_ropes",
      "pm_hydraulic_hoses", "loler_jack",
    ],
  },
  {
    area: "Governor and Safety Gear",
    ids: ["mr_governor_guard", "pit_safety_gear", "pm_safety_gear_governor", "loler_safety_gear"],
  },
  {
    area: "Car Interior and Fixtures",
    ids: ["car_floor", "car_toe_guard", "car_sharp_edges"],
  },
  {
    area: "Car Doors and Door Operator",
    ids: ["car_door_protection", "car_door_open_button", "pm_landing_car_doors", "pm_door_surrounds"],
  },
  {
    area: "Landing Doors and Locks",
    ids: ["mr_release_keys", "pit_lock_covers", "pit_landing_barriers", "pm_door_locks", "loler_landing_locks"],
  },
  {
    area: "Car Top and Shaft Equipment",
    ids: [
      "toc_supports", "toc_gaps", "toc_handrails", "toc_caged_light", "toc_clean",
      "toc_car_top_control", "toc_headroom", "toc_headroom_notice", "toc_sharp_edges",
      "pit_shaft_lighting", "pit_division_screen", "pit_lifting_beams",
    ],
  },
  {
    area: "Pit, Buffers and Safety Devices",
    ids: ["pit_access", "pit_socket", "pit_ladder", "pit_prop", "pit_depth_notice", "pm_buffers", "pm_pressure_relief_valve"],
  },
  {
    area: "Levelling and Ride Quality",
    ids: ["car_levelling", "pm_car_sling", "pm_guide_units"],
  },
  {
    area: "Alarm, Emergency Communication and Lighting",
    ids: ["car_emergency_light", "car_alarm", "mr_emergency_lighting", "loler_alarm_test"],
  },
  {
    area: "Safety Circuits and Stop Switches",
    ids: [
      "mr_push_stop", "toc_up_limit", "toc_push_stop", "pit_push_stop", "pm_shaft_safety_limits",
      "loler_shaft_switches", "pm_emergency_releases",
    ],
  },
  {
    area: "Lubrication and General Condition",
    ids: ["pm_car_guides", "pm_floor_selectors", "pm_cleaning", "gen_no_suspect_material", "gen_no_hazard"],
  },
  {
    area: "Final Test and Return to Service",
    // Includes the two ELE-MS-001 documentation checks (Session ADM): the end-of-visit
    // confirmations that the LOLER report is on site and the maintenance log is signed.
    ids: ["gen_switches", "pm_general_running", "doc_loler", "doc_log"],
  },
];

// The item ids that DETECT the ELE-MS-001 passenger checklist (mirrors the server rule): the
// two documentation checks are excluded because they also live in the goods / platform / scissor
// templates as the shared DOC_ITEMS, so matching on them would wrongly flip a goods lift onto
// the fourteen-area presentation.
const DETECTION_IDS: ReadonlySet<string> = new Set(
  SERVICE_AREAS_14.flatMap((a) => a.ids).filter((id) => id !== "doc_loler" && id !== "doc_log"),
);

export function itemsUseServiceAreas(items: { id: string }[]): boolean {
  return items.some((i) => DETECTION_IDS.has(i.id));
}

// One inspection area as the engineer works it: its (drive-type-filtered) checklist items.
// An area with NO applicable item for this lift's drive type has an empty items list and is
// shown as N/A -- never confirmed, never recorded as passed.
export interface AreaGroup {
  key: string; // stable key (the area/group label)
  label: string;
  items: ChecklistItemDef[];
}

// Group the VISIBLE (already drive-type-filtered) checklist items into the areas the engineer
// confirms. For the ELE-MS-001 passenger checklist that is the fixed fourteen customer-facing
// areas (in order, N/A areas included); any item outside the mapping (e.g. a tenant-override
// extra) is NEVER dropped -- it lands in a trailing "Other checks" area. A non-passenger
// checklist (goods / platform / scissor, or an old cached template) uses the caller-supplied
// fallback groups (its sections or categories) as the areas, so the one-tap-confirm flow and
// its integrity gate apply to every lift service. Pure.
export function buildAreaGroups(visibleItems: ChecklistItemDef[], fallbackGroups: { key: string; label: string; items: ChecklistItemDef[] }[]): AreaGroup[] {
  if (!itemsUseServiceAreas(visibleItems)) {
    return fallbackGroups.map((g) => ({ key: g.key, label: g.label, items: g.items }));
  }
  const byId = new Map(visibleItems.map((i) => [i.id, i]));
  const mapped = new Set<string>();
  const areas: AreaGroup[] = SERVICE_AREAS_14.map(({ area, ids }) => {
    const items = ids.map((id) => byId.get(id)).filter((i): i is ChecklistItemDef => !!i);
    for (const i of items) mapped.add(i.id);
    return { key: area, label: area, items };
  });
  const leftovers = visibleItems.filter((i) => !mapped.has(i.id));
  if (leftovers.length > 0) areas.push({ key: "Other checks", label: "Other checks", items: leftovers });
  return areas;
}

// The areas the engineer must confirm: every area with at least one applicable item. An
// empty (N/A) area is excluded -- it is shown as not applicable, never passed. Pure.
export function confirmableAreas(areas: AreaGroup[]): AreaGroup[] {
  return areas.filter((a) => a.items.length > 0);
}

export function allAreasConfirmed(areas: AreaGroup[], confirmed: Record<string, boolean>): boolean {
  return confirmableAreas(areas).every((a) => confirmed[a.key] === true);
}

export function unconfirmedAreaCount(areas: AreaGroup[], confirmed: Record<string, boolean>): number {
  return confirmableAreas(areas).filter((a) => confirmed[a.key] !== true).length;
}

// The display state of one area row.
//   na          -- no applicable item for this lift's drive type (nothing to inspect)
//   attention   -- at least one item flagged Fail or Advisory
//   pass        -- confirmed, nothing flagged (items marked N/A alone do not read as a problem)
//   unconfirmed -- not yet confirmed
export type AreaState = "na" | "attention" | "pass" | "unconfirmed";

export function areaState(area: AreaGroup, confirmed: Record<string, boolean>, statuses: Record<string, ChecklistStatus>): AreaState {
  if (area.items.length === 0) return "na";
  if (area.items.some((i) => statuses[i.id] === "fail" || statuses[i.id] === "advisory")) return "attention";
  return confirmed[area.key] === true ? "pass" : "unconfirmed";
}

// Genuine problems the engineer flagged (Fail or Advisory). Item-level N/A markings are
// recorded but are not "issues", so the submit label never over-counts. Pure.
export function countFlaggedIssues(items: ChecklistItemDef[], statuses: Record<string, ChecklistStatus>): number {
  return items.filter((i) => statuses[i.id] === "fail" || statuses[i.id] === "advisory").length;
}

// The first Fail in ONE area that is missing its required note (the per-area confirm gate),
// or null. Mirrors firstFailMissingNote scoped to the drilled-in area. Pure.
export function areaFirstFailMissingNote(
  area: AreaGroup,
  statuses: Record<string, ChecklistStatus>,
  notes: Record<string, string>,
): ChecklistItemDef | null {
  for (const i of area.items) {
    if (statuses[i.id] === "fail" && !notes[i.id]?.trim()) return i;
  }
  return null;
}

// ---- The office view of a STORED checklist (Session ADM Loop 2/3) -------------------------
// Groups a job sheet's stored checklist entries (the office DTO's ChecklistEntry rows) into
// the same fourteen areas, so the office reads the record exactly as the engineer captured it
// and the customer document presents it. Entries are matched by their stable item id; an entry
// with no id (or an unmapped id, e.g. a legacy free-text row) lands in "Other checks". Returns
// null when the stored entries are not the ELE-MS-001 passenger checklist (goods / platform /
// equipment / legacy) -- the office then keeps its flat list view. Pure, generic over the
// entry type so the UI keeps its full row objects.
export interface StoredAreaGroup<T> {
  key: string;
  label: string;
  entries: T[];
  fails: number;
  advisories: number;
  nas: number;
  passes: number;
}

export function groupStoredChecklistByArea<T extends { id?: string; status: string }>(entries: T[]): StoredAreaGroup<T>[] | null {
  if (!itemsUseServiceAreas(entries.filter((e): e is T & { id: string } => typeof e.id === "string"))) return null;
  const areaByItemId = new Map<string, string>();
  for (const a of SERVICE_AREAS_14) for (const id of a.ids) areaByItemId.set(id, a.area);
  const buckets = new Map<string, T[]>();
  for (const a of SERVICE_AREAS_14) buckets.set(a.area, []);
  buckets.set("Other checks", []);
  for (const e of entries) {
    const area = (e.id && areaByItemId.get(e.id)) || "Other checks";
    buckets.get(area)!.push(e);
  }
  const groups: StoredAreaGroup<T>[] = [];
  for (const [label, list] of buckets) {
    if (label === "Other checks" && list.length === 0) continue; // only shown when something landed there
    groups.push({
      key: label,
      label,
      entries: list,
      fails: list.filter((e) => e.status === "fail").length,
      advisories: list.filter((e) => e.status === "advisory").length,
      nas: list.filter((e) => e.status === "na").length,
      passes: list.filter((e) => e.status === "pass").length,
    });
  }
  return groups;
}

// The server caps JobSheet.engineerNotes at 5000 characters. The composed value is clamped to
// that cap so an aggregated set of area notes can never queue a completion the server would
// reject with a 400 (which the outbox would retry forever on a job that cannot be re-edited).
export const ENGINEER_NOTES_MAX = 5000;

// Compose the engineer-notes text for the completion payload: the engineer's own free-text
// notes first, then any per-area notes as "Area: note" lines, clamped to the server cap. Pure.
export function composeEngineerNotes(
  ownNotes: string,
  areas: AreaGroup[],
  areaNotes: Record<string, string>,
): string | undefined {
  const lines = [
    ownNotes.trim() || null,
    ...areas.map((a) => (areaNotes[a.key]?.trim() ? `${a.label}: ${areaNotes[a.key]!.trim()}` : null)),
  ].filter((l): l is string => !!l);
  if (lines.length === 0) return undefined;
  return lines.join("\n").slice(0, ENGINEER_NOTES_MAX).trim();
}
